import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Alert,
  CircularProgress,
  Tooltip,
  Divider,
  Autocomplete,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Stack,
  Pagination,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

import api from "../../services/api";
import departmentService from "../../services/departmentService";
import {
  listAssignments,
  createAssignment,
  updateAssignment,
  deleteAssignment,
  getAssignment,
  assignMentees,
  unassignMentee,
  listUnassignedStudents,
  listGfmClasses,
} from "../../services/gfmService";

/**
 * GFM (Guardian Faculty Member) — Assignments management page.
 *
 * Backing endpoints (services/gfmService.js), all gated by `gfm.manage`:
 *   GET    /gfm/assignments             → listAssignments({ isActive, page, pageSize })
 *   POST   /gfm/assignments             → createAssignment(body)
 *   GET    /gfm/assignments/:id         → getAssignment(id)  (assignment + mentees)
 *   PATCH  /gfm/assignments/:id         → updateAssignment(id, body)
 *   DELETE /gfm/assignments/:id         → deleteAssignment(id)
 *   POST   /gfm/assignments/:id/mentees → assignMentees(id, studentIds[])
 *   DELETE /gfm/mentees/:menteeId       → unassignMentee(menteeId)
 *   GET    /gfm/students/unassigned     → listUnassignedStudents({ classId, departmentId, search, page, pageSize })
 *
 * The route is wrapped in `PermissionBasedRoute requiredPermission="gfm.manage"`
 * (App.js), so this page assumes the caller already holds `gfm.manage`.
 *
 * Follows the no-crash frontend rules: every async call is wrapped in try/catch,
 * failures surface as a toast (and inline Alert where relevant) — never a white
 * screen — and all external response access is optional-chained with safe
 * defaults. Collections are guarded before iteration.
 *
 * _Requirements: 1.1, 1.5, 1.6, 2.1, 2.5, 2.7, 3.1, 3.2, 4.9, 10.1_
 */

const CADENCE_OPTIONS = [
  { value: "THREE_DAY", label: "Every 3 days" },
  { value: "WEEKLY", label: "Weekly" },
  { value: "MONTHLY", label: "Monthly" },
];

// Sentinel for the nullable ("use institute/system default") Select choices.
const DEFAULT_SENTINEL = "__default__";

const MENTEE_PAGE_SIZE = 25;

const cadenceLabel = (value) => {
  const match = CADENCE_OPTIONS.find((o) => o.value === value);
  return match ? match.label : value ?? "—";
};

// Extract a list array from a response body that may expose it under one of a
// few keys, or be an array itself. Always returns an array (never undefined).
const pickList = (data, keys = []) => {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];
  for (const key of keys) {
    if (Array.isArray(data[key])) return data[key];
  }
  return [];
};

// Best-effort extraction of a human name from a variety of teacher shapes.
const teacherDisplayName = (teacher) => {
  if (!teacher || typeof teacher !== "object") return "";
  const user = teacher.user || teacher.User || teacher;
  const first = user.firstName || user.first_name || "";
  const last = user.lastName || user.last_name || "";
  const full = `${first} ${last}`.trim();
  return (
    user.name ||
    user.fullName ||
    (full || null) ||
    user.email ||
    ""
  );
};

const GfmAssignments = () => {
  const currentUser = useSelector((s) => s?.auth?.user);
  const selectedInstitute = useSelector((s) => s?.instituteContext?.selectedInstitute);
  const instituteId =
    currentUser?.instituteId ?? selectedInstitute?.id ?? null;

  // ─── Assignment list state ───
  const [assignments, setAssignments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // ─── Teacher options (create dialog) ───
  const [teacherOptions, setTeacherOptions] = useState([]);
  const [teachersLoading, setTeachersLoading] = useState(false);

  // ─── Create / edit dialog ───
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = create mode
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    teacherUserId: null,
    isActive: true,
    notificationCadenceOverride: null,
    defaulterThresholdOverride: null,
  });
  const [thresholdError, setThresholdError] = useState(null);

  // ─── Mentee drawer state ───
  const [menteeDrawerOpen, setMenteeDrawerOpen] = useState(false);
  const [activeAssignment, setActiveAssignment] = useState(null);
  const [currentMentees, setCurrentMentees] = useState([]);
  const [menteesLoading, setMenteesLoading] = useState(false);

  // Filters + unassigned discovery
  const [classOptions, setClassOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  // Monotonic request id so only the LATEST class-list fetch may apply its result.
  // Prevents a slower/stale (or errored) response from clobbering the current one —
  // the cause of the "dropdown sometimes empty" race when the drawer opened.
  const classReqSeqRef = useRef(0);
  const [filterClassId, setFilterClassId] = useState("");
  const [filterDepartmentId, setFilterDepartmentId] = useState("");
  const [filterBatchCode, setFilterBatchCode] = useState("");
  const [classesLoading, setClassesLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [unassigned, setUnassigned] = useState([]);
  const [unassignedTotal, setUnassignedTotal] = useState(0);
  const [unassignedPage, setUnassignedPage] = useState(1); // 1-indexed
  const [unassignedLoading, setUnassignedLoading] = useState(false);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [assigningMentees, setAssigningMentees] = useState(false);
  const [assignSummary, setAssignSummary] = useState(null); // { assigned, skipped }

  // ─── Fetch assignments ───
  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await listAssignments({
        page: (paginationModel.page ?? 0) + 1,
        pageSize: paginationModel.pageSize ?? 10,
      });
      const data = res?.data?.data ?? res?.data ?? {};
      const items = pickList(data, ["items", "rows"]);
      setAssignments(items);
      setTotal(Number(data.total ?? items.length ?? 0));
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Failed to load GFM assignments.";
      setLoadError(msg);
      setAssignments([]);
      setTotal(0);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  // ─── Fetch teachers for the create dialog (lazy, on first open) ───
  const fetchTeachers = useCallback(async () => {
    if (teacherOptions.length > 0) return;
    setTeachersLoading(true);
    try {
      const params = { page: 1, limit: 500 };
      if (instituteId) params.instituteId = instituteId;
      const res = await api.get("/institute/get-all-teachers", { params });
      const raw =
        res?.data?.data?.teachers ||
        res?.data?.data?.rows ||
        res?.data?.data?.items ||
        res?.data?.data ||
        res?.data?.teachers ||
        res?.data ||
        [];
      const list = Array.isArray(raw) ? raw : [];
      const options = list
        .map((t) => {
          const userId =
            t?.userId ??
            t?.user?.id ??
            t?.User?.id ??
            t?.userDetails?.id ??
            t?.id ??
            null;
          const label = teacherDisplayName(t) || `User #${userId ?? "?"}`;
          return userId == null ? null : { userId, label };
        })
        .filter(Boolean);
      setTeacherOptions(options);
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Failed to load teachers list.";
      toast.error(msg);
      setTeacherOptions([]);
    } finally {
      setTeachersLoading(false);
    }
  }, [teacherOptions.length, instituteId]);

  // ─── Load classes for the Class dropdown, scoped to a department ───
  // Uses the GFM endpoint (GET /gfm/classes) so acting-institute context is
  // threaded for Super Admin. Passing a departmentId scopes the list; omitting
  // it returns every class in the institute. Each row carries `practicalBatches`
  // so the Batch dropdown can be derived from the selected class.
  const loadClassesForDepartment = useCallback(async (departmentId) => {
    const reqId = ++classReqSeqRef.current;
    setClassesLoading(true);
    try {
      const params = {};
      if (departmentId) params.departmentId = departmentId;
      const res = await listGfmClasses(params);
      // Ignore this response if a newer request has since been fired — otherwise a
      // slow/stale reply could overwrite the current department's class list.
      if (reqId !== classReqSeqRef.current) return;
      const data = res?.data?.data ?? res?.data ?? [];
      setClassOptions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(
        "[GfmAssignments] loadClassesForDepartment failed:",
        err?.message || err
      );
      // Only the latest request may clear the list on error.
      if (reqId === classReqSeqRef.current) setClassOptions([]);
    } finally {
      if (reqId === classReqSeqRef.current) setClassesLoading(false);
    }
  }, []);

  // ─── Fetch department options (lazy, on drawer open) ───
  // NOTE: the class list is loaded solely by the effect below (single source of
  // truth). Do NOT also load classes here — two concurrent loaders on open was the
  // race that left the Class dropdown intermittently empty.
  const fetchFilterOptions = useCallback(async () => {
    try {
      const deptRes = await (instituteId
        ? departmentService.getDepartmentNames(instituteId)
        : departmentService.getDepartmentNames()
      ).catch(() => null);
      const deptData = deptRes?.data?.data || deptRes?.data || [];
      setDepartmentOptions(Array.isArray(deptData) ? deptData : []);
    } catch (err) {
      // Non-fatal — filters simply stay empty.
      console.error(
        "[GfmAssignments] fetchFilterOptions failed:",
        err?.message || err
      );
    }
  }, [instituteId]);

  // Single loader for the Class list: runs on drawer open and on every department
  // change. The request-sequence guard in loadClassesForDepartment ensures only the
  // latest fetch applies, so rapid open/department changes can't leave it empty.
  useEffect(() => {
    if (!menteeDrawerOpen) return;
    loadClassesForDepartment(filterDepartmentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menteeDrawerOpen, filterDepartmentId]);

  // Batch options are derived from the currently-selected class's
  // `practicalBatches`. When no class is selected, no batch options are shown.
  const batchOptions = useMemo(() => {
    if (!filterClassId) return [];
    const selected = classOptions.find(
      (c) => String(c?.id ?? c?.value) === String(filterClassId)
    );
    const batches = Array.isArray(selected?.practicalBatches)
      ? selected.practicalBatches
      : [];
    return batches.filter((b) => typeof b === "string" && b.trim() !== "");
  }, [filterClassId, classOptions]);

  // ─── Create / edit dialog helpers ───
  const openCreateDialog = () => {
    setEditingId(null);
    setForm({
      teacherUserId: null,
      isActive: true,
      notificationCadenceOverride: null,
      defaulterThresholdOverride: null,
    });
    setThresholdError(null);
    setDialogOpen(true);
    fetchTeachers();
  };

  const openEditDialog = (row) => {
    if (!row) return;
    setEditingId(row.id);
    setForm({
      teacherUserId: row.teacherUserId ?? null,
      isActive: row.isActive ?? true,
      notificationCadenceOverride: row.notificationCadenceOverride ?? null,
      defaulterThresholdOverride:
        row.defaulterThresholdOverride ?? null,
    });
    setThresholdError(null);
    setDialogOpen(true);
    fetchTeachers();
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
  };

  const thresholdUsesDefault =
    form.defaulterThresholdOverride == null ||
    form.defaulterThresholdOverride === "";

  const validateForm = () => {
    if (!editingId && form.teacherUserId == null) {
      toast.error("Please select a teacher.");
      return false;
    }
    if (
      form.defaulterThresholdOverride != null &&
      form.defaulterThresholdOverride !== ""
    ) {
      const n = Number(form.defaulterThresholdOverride);
      if (!Number.isInteger(n) || n < 0 || n > 100) {
        setThresholdError(
          "Threshold override must be a whole number between 0 and 100."
        );
        return false;
      }
    }
    return true;
  };

  const handleSaveAssignment = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const thresholdOverride =
        form.defaulterThresholdOverride == null ||
        form.defaulterThresholdOverride === ""
          ? null
          : Number(form.defaulterThresholdOverride);

      if (editingId) {
        const patch = {
          isActive: form.isActive,
          notificationCadenceOverride: form.notificationCadenceOverride,
          defaulterThresholdOverride: thresholdOverride,
        };
        await updateAssignment(editingId, patch);
        toast.success("Assignment updated.");
      } else {
        const body = {
          teacherUserId: form.teacherUserId,
          isActive: form.isActive,
          notificationCadenceOverride: form.notificationCadenceOverride,
          defaulterThresholdOverride: thresholdOverride,
        };
        await createAssignment(body);
        toast.success("Assignment created.");
      }
      setDialogOpen(false);
      fetchAssignments();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        (editingId
          ? "Failed to update assignment."
          : "Failed to create assignment.");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAssignment = async (row) => {
    if (!row?.id) return;
    const name = teacherDisplayName(row.teacher) || `#${row.id}`;
    // eslint-disable-next-line no-alert
    if (
      !window.confirm(
        `Delete the GFM assignment for ${name}? This also removes its mentee links.`
      )
    ) {
      return;
    }
    try {
      await deleteAssignment(row.id);
      toast.success("Assignment deleted.");
      fetchAssignments();
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Failed to delete assignment.";
      toast.error(msg);
    }
  };

  // ─── Mentee drawer ───
  const openMenteeDrawer = async (row) => {
    if (!row?.id) return;
    setActiveAssignment(row);
    setMenteeDrawerOpen(true);
    setAssignSummary(null);
    setSelectedStudentIds([]);
    setFilterClassId("");
    setFilterDepartmentId("");
    setFilterBatchCode("");
    setSearchInput("");
    setSearchTerm("");
    setUnassignedPage(1);
    fetchFilterOptions();
    await loadMentees(row.id);
  };

  const closeMenteeDrawer = () => {
    if (assigningMentees) return;
    setMenteeDrawerOpen(false);
    setActiveAssignment(null);
    setCurrentMentees([]);
    setUnassigned([]);
    setUnassignedTotal(0);
    setSelectedStudentIds([]);
    setAssignSummary(null);
  };

  const loadMentees = useCallback(async (assignmentId) => {
    if (!assignmentId) return;
    setMenteesLoading(true);
    try {
      const res = await getAssignment(assignmentId);
      const data = res?.data?.data ?? res?.data ?? {};
      const mentees = Array.isArray(data.mentees) ? data.mentees : [];
      setCurrentMentees(mentees);
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Failed to load current mentees.";
      toast.error(msg);
      setCurrentMentees([]);
    } finally {
      setMenteesLoading(false);
    }
  }, []);

  const fetchUnassigned = useCallback(async () => {
    if (!menteeDrawerOpen) return;
    setUnassignedLoading(true);
    try {
      const params = {
        page: unassignedPage,
        pageSize: MENTEE_PAGE_SIZE,
      };
      if (filterClassId) params.classId = filterClassId;
      if (filterDepartmentId) params.departmentId = filterDepartmentId;
      if (filterBatchCode) params.practicalBatchCode = filterBatchCode;
      if (searchTerm) params.search = searchTerm;
      const res = await listUnassignedStudents(params);
      const data = res?.data?.data ?? res?.data ?? {};
      const items = pickList(data, ["items"]);
      setUnassigned(items);
      setUnassignedTotal(Number(data.total ?? items.length ?? 0));
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "Failed to load unassigned students.";
      toast.error(msg);
      setUnassigned([]);
      setUnassignedTotal(0);
    } finally {
      setUnassignedLoading(false);
    }
  }, [
    menteeDrawerOpen,
    unassignedPage,
    filterClassId,
    filterDepartmentId,
    filterBatchCode,
    searchTerm,
  ]);

  useEffect(() => {
    fetchUnassigned();
  }, [fetchUnassigned]);

  const handleApplySearch = () => {
    setUnassignedPage(1);
    setSearchTerm(searchInput.trim());
  };

  const handleFilterChange = (setter) => (e) => {
    setUnassignedPage(1);
    setSelectedStudentIds([]);
    setter(e.target.value);
  };

  // Department change: reset page/selection AND clear the dependent Class + Batch
  // filters (the class list reloads for the new department via the effect above).
  const handleDepartmentChange = (e) => {
    setUnassignedPage(1);
    setSelectedStudentIds([]);
    setFilterClassId("");
    setFilterBatchCode("");
    setFilterDepartmentId(e.target.value);
  };

  // Class change: reset page/selection AND clear the dependent Batch filter
  // (batch options are derived from the selected class).
  const handleClassChange = (e) => {
    setUnassignedPage(1);
    setSelectedStudentIds([]);
    setFilterBatchCode("");
    setFilterClassId(e.target.value);
  };

  const studentIdOf = (s) =>
    s?.studentId ?? s?.id ?? s?.student?.id ?? null;

  const toggleStudent = (id) => {
    if (id == null) return;
    setSelectedStudentIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const allPageSelected =
    unassigned.length > 0 &&
    unassigned.every((s) => selectedStudentIds.includes(studentIdOf(s)));

  const toggleSelectAllOnPage = () => {
    const pageIds = unassigned
      .map((s) => studentIdOf(s))
      .filter((x) => x != null);
    if (allPageSelected) {
      setSelectedStudentIds((prev) =>
        prev.filter((id) => !pageIds.includes(id))
      );
    } else {
      setSelectedStudentIds((prev) =>
        Array.from(new Set([...prev, ...pageIds]))
      );
    }
  };

  const handleAssignMentees = async () => {
    if (!activeAssignment?.id) return;
    if (!Array.isArray(selectedStudentIds) || selectedStudentIds.length === 0) {
      toast.info("Select at least one student to assign.");
      return;
    }
    setAssigningMentees(true);
    setAssignSummary(null);
    try {
      const res = await assignMentees(
        activeAssignment.id,
        selectedStudentIds
      );
      const data = res?.data?.data ?? res?.data ?? {};
      const assigned = Array.isArray(data.assigned) ? data.assigned : [];
      const skipped = Array.isArray(data.skipped) ? data.skipped : [];
      setAssignSummary({ assigned, skipped });
      if (assigned.length > 0) {
        const noun = assigned.length === 1 ? "student" : "students";
        const skippedNote = skipped.length
          ? `, skipped ${skipped.length}.`
          : ".";
        toast.success(`Assigned ${assigned.length} ${noun}${skippedNote}`);
      } else if (skipped.length > 0) {
        toast.warn(`No students assigned — ${skipped.length} skipped.`);
      }
      setSelectedStudentIds([]);
      // Refresh current mentees, the unassigned pool, and the row's count.
      await loadMentees(activeAssignment.id);
      fetchUnassigned();
      fetchAssignments();
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Failed to assign mentees.";
      toast.error(msg);
    } finally {
      setAssigningMentees(false);
    }
  };

  const handleUnassignMentee = async (mentee) => {
    const menteeId = mentee?.id;
    if (!menteeId) return;
    try {
      await unassignMentee(menteeId);
      toast.success("Mentee unassigned.");
      await loadMentees(activeAssignment?.id);
      fetchUnassigned();
      fetchAssignments();
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Failed to unassign mentee.";
      toast.error(msg);
    }
  };

  // ─── DataGrid columns ───
  const columns = useMemo(
    () => [
      {
        field: "teacher",
        headerName: "Teacher",
        flex: 1.4,
        minWidth: 180,
        sortable: false,
        valueGetter: (params) =>
          teacherDisplayName(params?.row?.teacher) ||
          (params?.row?.teacherUserId != null
            ? `User #${params.row.teacherUserId}`
            : "—"),
      },
      {
        field: "isActive",
        headerName: "Active",
        width: 110,
        sortable: false,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params?.row?.isActive ? "Active" : "Inactive"}
            color={params?.row?.isActive ? "success" : "default"}
          />
        ),
      },
      {
        field: "cadence",
        headerName: "Cadence",
        width: 140,
        sortable: false,
        valueGetter: (params) => {
          const eff = params?.row?.effective || {};
          return cadenceLabel(eff.cadence);
        },
      },
      {
        field: "threshold",
        headerName: "Threshold",
        width: 120,
        sortable: false,
        valueGetter: (params) => {
          const eff = params?.row?.effective || {};
          return eff.threshold == null ? "—" : `${eff.threshold}%`;
        },
      },
      {
        field: "menteeCount",
        headerName: "Mentees",
        width: 110,
        sortable: false,
        valueGetter: (params) => Number(params?.row?.menteeCount ?? 0),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 170,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box>
            <Tooltip title="Manage mentees">
              <IconButton
                size="small"
                onClick={() => openMenteeDrawer(params.row)}
              >
                <GroupAddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => openEditDialog(params.row)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDeleteAssignment(params.row)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const studentLabel = (s) => {
    const name = s?.name || s?.studentName || "";
    const rollNumber = s?.rollNumber || s?.roll || "";
    const className = s?.className || s?.class || "";
    const bits = [name || `Student #${studentIdOf(s) ?? "?"}`];
    if (rollNumber) bits.push(`(${rollNumber})`);
    if (className) bits.push(`• ${className}`);
    return bits.join(" ");
  };

  const menteeLabel = (m) => {
    const student = m?.student || m || {};
    const first = student.firstName || "";
    const last = student.lastName || "";
    const full = `${first} ${last}`.trim();
    return (
      student.name ||
      (full || null) ||
      student.enrollmentId ||
      `Student #${m?.studentId ?? "?"}`
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Guardian Faculty Assignments
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Assign teachers as guardians and manage their mentee lists.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchAssignments}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
          >
            New Assignment
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: { xs: 1, md: 2 }, mt: 1 }}>
        {loadError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setLoadError(null)}
          >
            {loadError}
          </Alert>
        )}
        <Box sx={{ width: "100%" }}>
          <DataGrid
            autoHeight
            rows={Array.isArray(assignments) ? assignments : []}
            columns={columns}
            getRowId={(row) => row?.id ?? row?.uuid ?? row?.teacherUserId}
            loading={loading}
            rowCount={total}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            disableColumnMenu
            localeText={{ noRowsLabel: "No GFM assignments yet." }}
            sx={{ border: 0 }}
          />
        </Box>
      </Paper>

      {/* ─── Create / Edit dialog ─── */}
      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {editingId ? "Edit GFM Assignment" : "New GFM Assignment"}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <Autocomplete
                options={teacherOptions}
                loading={teachersLoading}
                disabled={!!editingId}
                getOptionLabel={(o) => o?.label ?? ""}
                isOptionEqualToValue={(o, v) => o?.userId === v?.userId}
                value={
                  teacherOptions.find(
                    (o) => o.userId === form.teacherUserId
                  ) || null
                }
                onChange={(_e, val) =>
                  setForm((f) => ({
                    ...f,
                    teacherUserId: val?.userId ?? null,
                  }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Teacher (guardian)"
                    size="small"
                    helperText={
                      editingId
                        ? "Teacher cannot be changed after creation."
                        : "Select the teacher who will act as guardian."
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {teachersLoading ? (
                            <CircularProgress color="inherit" size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="cadence-override-label">
                  Cadence override
                </InputLabel>
                <Select
                  labelId="cadence-override-label"
                  label="Cadence override"
                  value={form.notificationCadenceOverride ?? DEFAULT_SENTINEL}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      notificationCadenceOverride:
                        e.target.value === DEFAULT_SENTINEL
                          ? null
                          : e.target.value,
                    }))
                  }
                >
                  <MenuItem value={DEFAULT_SENTINEL}>
                    <em>Use institute default</em>
                  </MenuItem>
                  {CADENCE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={thresholdUsesDefault}
                    onChange={(e) => {
                      setThresholdError(null);
                      setForm((f) => ({
                        ...f,
                        defaulterThresholdOverride: e.target.checked
                          ? null
                          : 75,
                      }));
                    }}
                    color="primary"
                  />
                }
                label="Threshold: use institute default"
              />
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Threshold override (%)"
                value={
                  thresholdUsesDefault
                    ? ""
                    : form.defaulterThresholdOverride
                }
                onChange={(e) => {
                  setThresholdError(null);
                  const raw = e.target.value;
                  setForm((f) => ({
                    ...f,
                    defaulterThresholdOverride:
                      raw === "" ? "" : Number(raw),
                  }));
                }}
                disabled={thresholdUsesDefault}
                error={!!thresholdError}
                helperText={thresholdError || "0–100"}
                inputProps={{ min: 0, max: 100, step: 1 }}
                sx={{ mt: 1 }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.isActive}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, isActive: e.target.checked }))
                    }
                    color="primary"
                  />
                }
                label="Active"
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block" }}
              >
                Only one active assignment is allowed per teacher.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveAssignment}
            disabled={saving}
            startIcon={
              saving ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {editingId ? "Save Changes" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Mentee Dialog (Centered Modal) ─── */}
      <Dialog
        open={menteeDrawerOpen}
        onClose={closeMenteeDrawer}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">
            Mentees —{" "}
            {teacherDisplayName(activeAssignment?.teacher) ||
              (activeAssignment?.teacherUserId != null
                ? `User #${activeAssignment.teacherUserId}`
                : "")}
          </Typography>
          <IconButton onClick={closeMenteeDrawer} disabled={assigningMentees}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3 }}>
          {/* Current mentees */}
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Current mentees {menteesLoading ? "" : `(${currentMentees.length})`}
          </Typography>
          {menteesLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={22} />
            </Box>
          ) : currentMentees.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No mentees assigned yet.
            </Typography>
          ) : (
            <List
              dense
              sx={{
                maxHeight: 160,
                overflow: "auto",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                mb: 2,
              }}
            >
              {currentMentees.map((m) => (
                <ListItem
                  key={m?.id ?? m?.uuid ?? menteeLabel(m)}
                  secondaryAction={
                    <Tooltip title="Unassign">
                      <IconButton
                        edge="end"
                        size="small"
                        color="error"
                        onClick={() => handleUnassignMentee(m)}
                      >
                        <PersonRemoveIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <ListItemText primary={menteeLabel(m)} />
                </ListItem>
              ))}
            </List>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Filters */}
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            Add mentees
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="filter-dept-label">Department</InputLabel>
                <Select
                  labelId="filter-dept-label"
                  label="Department"
                  value={filterDepartmentId}
                  onChange={handleDepartmentChange}
                >
                  <MenuItem value="">
                    <em>All departments</em>
                  </MenuItem>
                  {departmentOptions.map((d) => (
                    <MenuItem key={d?.id ?? d?.value} value={d?.id ?? d?.value}>
                      {d?.name ?? d?.label ?? d?.departmentName ?? `#${d?.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="filter-class-label">Class</InputLabel>
                <Select
                  labelId="filter-class-label"
                  label="Class"
                  value={filterClassId}
                  onChange={handleClassChange}
                >
                  <MenuItem value="">
                    <em>{classesLoading ? "Loading classes…" : "All classes"}</em>
                  </MenuItem>
                  {classOptions.map((c) => {
                    const name = c?.name ?? c?.className ?? c?.label ?? `#${c?.id}`;
                    const section = c?.section ? ` (${c.section})` : "";
                    return (
                      <MenuItem key={c?.id ?? c?.value} value={c?.id ?? c?.value}>
                        {`${name}${section}`}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small" disabled={!filterClassId || batchOptions.length === 0}>
                <InputLabel id="filter-batch-label">Batch</InputLabel>
                <Select
                  labelId="filter-batch-label"
                  label="Batch"
                  value={filterBatchCode}
                  onChange={handleFilterChange(setFilterBatchCode)}
                >
                  <MenuItem value="">
                    <em>
                      {!filterClassId
                        ? "Select a class first"
                        : batchOptions.length === 0
                        ? "No batches"
                        : "All batches"}
                    </em>
                  </MenuItem>
                  {batchOptions.map((b) => (
                    <MenuItem key={b} value={b}>
                      {b}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search name / enrollment"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleApplySearch();
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={handleApplySearch}
                  startIcon={<SearchIcon />}
                >
                  Search
                </Button>
              </Box>
            </Grid>
          </Grid>

          {/* Assign summary */}
          {assignSummary && (
            <Alert
              severity={
                (assignSummary.assigned?.length ?? 0) > 0 ? "success" : "info"
              }
              sx={{ mb: 2 }}
              onClose={() => setAssignSummary(null)}
            >
              Assigned {assignSummary.assigned?.length ?? 0}
              {(assignSummary.skipped?.length ?? 0) > 0 && (
                <>
                  , skipped {assignSummary.skipped.length}:
                  <Box component="ul" sx={{ pl: 2, m: 0, mt: 0.5 }}>
                    {assignSummary.skipped.map((s, i) => (
                      <li key={`${s?.studentId ?? i}`}>
                        Student #{s?.studentId ?? "?"} — {s?.reason || "skipped"}
                      </li>
                    ))}
                  </Box>
                </>
              )}
            </Alert>
          )}

          {/* Unassigned list */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Unassigned students {unassignedLoading ? "" : `(${unassignedTotal})`}
            </Typography>
            {unassigned.length > 0 && (
              <Button size="small" onClick={toggleSelectAllOnPage}>
                {allPageSelected ? "Clear page" : "Select page"}
              </Button>
            )}
          </Box>

          {unassignedLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={22} />
            </Box>
          ) : unassigned.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No unassigned students match these filters.
            </Typography>
          ) : (
            <List
              dense
              sx={{
                maxHeight: 260,
                overflow: "auto",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              {unassigned.map((s) => {
                const id = studentIdOf(s);
                return (
                  <ListItem
                    key={id ?? studentLabel(s)}
                    onClick={() => toggleStudent(id)}
                    sx={{
                      cursor: "pointer",
                      alignItems: "center",
                      py: 0.75,
                      px: 1.5,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Checkbox
                        size="small"
                        checked={selectedStudentIds.includes(id)}
                        tabIndex={-1}
                        disableRipple
                        sx={{ p: 0.5 }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={studentLabel(s)}
                      sx={{ my: 0 }}
                    />
                  </ListItem>
                );
              })}
            </List>
          )}

          {unassignedTotal > MENTEE_PAGE_SIZE && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination
                size="small"
                count={Math.max(
                  1,
                  Math.ceil(unassignedTotal / MENTEE_PAGE_SIZE)
                )}
                page={unassignedPage}
                onChange={(_e, p) => setUnassignedPage(p)}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeMenteeDrawer} disabled={assigningMentees}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={
              assigningMentees ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <GroupAddIcon />
              )
            }
            onClick={handleAssignMentees}
            disabled={assigningMentees || selectedStudentIds.length === 0}
          >
            Assign selected ({selectedStudentIds.length})
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GfmAssignments;
